import React, { FC, ReactNode, useEffect, useMemo, useRef, useState, isValidElement } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';

import { buttonVariants, buttonWidths } from '@clatter/ui';
import { isDarkColor } from '../../../helpers';
import { CircularLoader } from '../../../shared';

const StyledButton = styled.button`
  border-radius: ${({ theme }) => theme?.radius?.button};
  padding: 0 10px;
  height: 36px;
  border: 1px solid;
  font-size: 13px;
  font-weight: 700;
  font-family: ${({ theme }) => theme?.fontFamily?.main ?? 'sans-serif'};

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  transition: background-color 0.3s ease, filter 0.3s ease;

  &.${buttonWidths.full} {
    width: 100%;
    span {
      margin: 0 auto;
    }
  }

  &:hover:not(:disabled) {
    background-color: #48c5eb;
  }
  &:disabled {
    background-color: #b3eafc;
    cursor: not-allowed;
  }

  &.button-primary {
    background-color: ${({ theme }) => theme?.palette?.primaryButton};
    color: ${({ theme }) =>
      isDarkColor(theme?.palette?.primaryButton) ? theme?.palette?.white : theme?.palette?.text};
    border-color: ${({ theme }) => theme?.palette?.primaryButton};
    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  }

  &.button-secondary {
    background-color: ${({ theme }) => theme?.palette?.secondaryColor};
    border-color: ${({ theme }) => theme?.palette?.secondaryColor};
    color: ${({ theme }) => theme?.palette?.white};
  }

  &.${buttonVariants.clear} {
    color: #262626;
    background-color: ${({ theme }) => theme?.palette?.white};
    border-color: ${({ theme }) =>
      isDarkColor(theme?.palette?.darkGrey) ? theme?.palette?.white : theme?.palette?.darkGrey};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme?.palette?.white};
      border-color: ${({ theme }) => theme?.palette?.accent};
      transition: all 0.3s ease-in-out;
    }
  }

  &.${buttonVariants.danger} {
    background-color: ${({ theme }) => theme?.palette?.danger};
    border-color: ${({ theme }) => theme?.palette?.danger};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme?.palette?.white};
      color: ${({ theme }) => theme?.palette?.danger};
    }
  }

  &.${buttonVariants.dark} {
    background-color: ${({ theme }) => theme?.palette?.black};
    border-color: ${({ theme }) => theme?.palette?.black};
    color: ${({ theme }) => theme?.palette?.white};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme?.palette?.white};
      color: ${({ theme }) => theme?.palette?.black};
    }
  }

  &.${buttonVariants.success} {
    background-color: ${({ theme }) => theme?.palette?.success};
    border-color: ${({ theme }) => theme?.palette?.success};
    color: ${({ theme }) => theme?.palette?.white};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme?.palette?.success};
      filter: brightness(0.87);
    }
  }

  &.${buttonVariants.warning} {
    background-color: ${({ theme }) => theme?.palette?.warning};
    border-color: ${({ theme }) => theme?.palette?.warning};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme?.palette?.white};
      color: ${({ theme }) => theme?.palette?.warning};
    }
  }

  &:disabled {
    border-color: ${({ theme }) => theme?.palette?.border};
    color: ${({ theme }) => theme?.palette?.border};
    background-color: ${({ theme }) => theme?.palette?.white};

    .loader-container svg {
      color: ${({ theme }) => theme?.palette?.border};
    }
  }

  &.${buttonWidths.xs} {
    height: 26px;
    padding: 0 ${({ theme }) => theme?.spacing?.small};
    font-size: 11px;
  }
  &.${buttonWidths.sm} {
    padding: 0 ${({ theme }) => theme?.spacing?.medium};
  }
  &.${buttonWidths.md} {
    min-width: 10rem;
    padding: 0 ${({ theme }) => theme?.spacing?.large};
  }
  &.${buttonWidths.lg} {
    padding: 0 66px;
  }

  .button-icon {
    font-size: 17px;
    /* + span {
      margin-left: ${({ theme }) => theme?.spacing?.small};
    } */
  }

  &.has-menu-items {
    padding: 0;
    padding-left: 12px;
  }

  .loader-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    svg {
      color: ${({ theme }) => theme?.palette?.white};
    }
  }

  &.is-loading {
    pointer-events: none;
    height: auto;
    min-height: 36px;
    padding: 8px 10px;
  }
`;

const StyledMenuContainer = styled.div<{ $full?: boolean }>`
  position: relative;
  display: inline-flex;
  ${({ $full }) => $full && 'width: 100%;'}
`;

const StyledButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme?.spacing?.small || '8px'};
  padding: 0 6px;
`;

const StyledButtonIcon = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 17px;
  padding: 0 8px 0 0;
`;

const StyledMenu = styled.div`
  position: absolute;
  display: none;
  background-color: ${({ theme }) => theme?.palette?.white};
  border-radius: ${({ theme }) => theme?.radius?.popupMenu};
  z-index: 2;

  width: max-content;
  white-space: nowrap;

  top: 42px;
  right: 0;
  border: 1px solid ${({ theme }) => theme?.palette?.grey};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);

  &.opened {
    display: block;
  }
`;

const StyledMenuItem = styled.div`
  cursor: pointer;
  padding: 8px 14px;
  font-size: 13px;

  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background-color: ${({ theme }) => theme?.palette?.hoverBackgroundColor};
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .menu-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    font-size: 18px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const StyledMenuDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme?.palette?.grey};
  margin: 4px 0;
`;

const StyledMenuHeader = styled.div`
  padding: 6px 14px 4px;
  font-size: 10px;
  font-weight: 700;
  /* color: ${({ theme }) => theme?.palette?.darkGrey ?? '#4B5563'}; */
  color: #4b5563;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

type MenuAction = {
  type: string; // e.g., "link" | "upload" | "delete" | ...
  payload?: Record<string, any>;
};

export type NgButtonMenuItem = {
  label?: string;
  action?: MenuAction; // always { type, payload }
  testId?: string;
  divider?: boolean;
  header?: boolean;
  disabled?: boolean;
  badge?: ReactNode;
  icon?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> | React.ComponentType<any> | ReactNode;
};

interface NgButtonProps {
  children?: ReactNode;
  disabled?: boolean;
  variant?: string;
  width?: string;
  testId?: string;
  isLoading?: boolean;
  loadingSlot?: ReactNode;
  icon?: any;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (action?: MenuAction) => void; // returns the action object
  menuItems?: NgButtonMenuItem[];
}

export const NgButton: FC<NgButtonProps> = ({
  children,
  onClick,
  type = 'button',
  isLoading,
  loadingSlot,
  width = buttonWidths.md,
  variant = buttonVariants.primary,
  disabled = false,
  testId,
  icon: Icon = null,
  menuItems,
}) => {
  const [opened, setOpened] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const hasMenu = useMemo(() => Array.isArray(menuItems) && menuItems.length > 0, [menuItems]);
  const actionableItems = useMemo(() => (menuItems || []).filter((i) => !i.divider && !i.header), [menuItems]);
  const hasMultipleMenuItems = hasMenu && actionableItems.length > 1;

  const handleRootClick = () => {
    if (disabled) return;
    if (!hasMenu) {
      onClick?.();
      return;
    }
    if (hasMultipleMenuItems) {
      setOpened((prev) => !prev);
      return;
    }
    // Single actionable item: return its action
    const only = actionableItems[0];
    onClick?.(only?.action);
  };

  const handleMenuItemClick = (action?: MenuAction) => {
    if (disabled) return;
    onClick?.(action);
    setOpened(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (opened && ref.current && !ref.current.contains(e.target as Node)) setOpened(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [opened]);

  return (
    <StyledMenuContainer ref={ref} $full={width === buttonWidths.full}>
      <StyledButton
        className={classNames(variant, width, { 'is-loading': isLoading, 'has-menu-items': hasMultipleMenuItems })}
        disabled={disabled || isLoading}
        type={type}
        onClick={handleRootClick}
        data-test-id={testId}
        aria-haspopup={hasMultipleMenuItems ? 'menu' : undefined}
        aria-expanded={hasMultipleMenuItems ? opened : undefined}
      >
        {isLoading && (
          <span className="loader-container">
            {loadingSlot ?? <CircularLoader size={20} />}
          </span>
        )}
        <span style={{ display: isLoading ? 'none' : 'contents' }}>
          {!!Icon && <Icon className="button-icon" />}

          <StyledButtonContent>{!!children && <span>{children}</span>}</StyledButtonContent>

          {hasMultipleMenuItems && (
            <StyledButtonIcon className={variant} aria-hidden>
              {opened ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </StyledButtonIcon>
          )}
        </span>
      </StyledButton>

      {hasMultipleMenuItems && (
        <StyledMenu className={classNames({ opened })} role="menu">
          {menuItems!.map(
            ({ label, action, divider, header, disabled: itemDisabled, badge, testId, icon: ItemIcon }, index) => {
              if (divider) {
                return <StyledMenuDivider key={`divider-${index}`} role="separator" aria-orientation="horizontal" />;
              }
              if (header) {
                return <StyledMenuHeader key={`header-${index}`}>{label}</StyledMenuHeader>;
              }

              const isDisabled = disabled || itemDisabled;

              let iconNode: ReactNode = null;
              if (ItemIcon) {
                if (isValidElement(ItemIcon)) {
                  iconNode = <span className="menu-icon">{ItemIcon}</span>;
                } else if (typeof ItemIcon === 'function' || typeof ItemIcon === 'object') {
                  const AsComponent: any = ItemIcon as any;
                  iconNode = <AsComponent className="menu-icon" />;
                }
              }

              return (
                <StyledMenuItem
                  key={`item-${index}`}
                  className={classNames({ disabled: isDisabled })}
                  onClick={() => !isDisabled && handleMenuItemClick(action)}
                  data-test-id={testId}
                  role="menuitem"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!isDisabled) handleMenuItemClick(action);
                    }
                  }}
                >
                  {iconNode}
                  {label}
                  {badge && <span style={{ marginLeft: 'auto', paddingLeft: 8 }}>{badge}</span>}
                </StyledMenuItem>
              );
            },
          )}
        </StyledMenu>
      )}
    </StyledMenuContainer>
  );
};
